import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import { displayAlertBoxSagaAction } from 'commons/src/actions';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import DropdownOptions from 'commons/src/components/dropdown/DropdownOptions';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { roleRestrictions } from 'commons/src/constants';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { MenuItem } from 'commons/src/models/menuModels';
import { toErrorTypeWithMessage } from 'commons/src/sagas/isErrorType';
import { updateThresholdsApi } from '../../../api/thresholdsApi';
import styles from './CustomThresholdSection.module.scss';

interface MockInterface {
    name: string;
    sensors: SensorTypes[];
    id: string;
}

const mockData: MockInterface[] = [
    {
        name: 'Airthings Office',
        id: 'd165d34b-2189-4aa5-9f72-5e687369f7e5',
        sensors: [SensorTypes.temp, SensorTypes.co2, SensorTypes.humidity, SensorTypes.voc],
    },
    {
        name: 'Bergen building',
        id: '476ea41d-337d-45d6-88ed-3ae400e559a0',
        sensors: [SensorTypes.temp],
    },
];

export const CustomThresholdSection = (): React.ReactElement => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [items, setItems] = useState<MockInterface[]>(mockData);
    // can we use react RTK here?
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const redirect = (locationId: string): void => {
        const url: string = generatePath('/buildings/:locationId/settings', { locationId });
        navigate(url);
    };

    const removeThresholds = useCallback(async (locationId: string): Promise<void> => {
        // TODO Loading on dropdown button? Alert box on success?
        await updateThresholdsApi({
            id: locationId,
            resourceScope: ThresholdsResourceScope.LOCATION,
            sensors: [],
        })
            .then((): void => {
                // update state as well
                setItems(items.filter(item => item.id !== locationId));
            })
            .catch(err => {
                dispatch(displayAlertBoxSagaAction(toErrorTypeWithMessage(err).message, true, true));
            });
    }, []);

    const onRemoveThresholds = (locationId: string): void => {
        removeThresholds(locationId).catch();
    };

    return (
        <div className={styles.body}>
            <div className={styles.header}>
                <div className={styles.info}>
                    <MaterialIcon name="info" extraClass={styles.infoIcon} />
                    <div>
                        <Trans
                            i18nKey={txt('CustomThresholds.AccountLocationDescription', {
                                count: items.length,
                            })}
                            components={{ bold: <span className={styles.bold} /> }}
                        />
                    </div>
                </div>
                {items.length > 0 && (
                    <TertiaryButton
                        onClick={(): void => setIsExpanded(!isExpanded)}
                        testAttr="thresholds-items-expand"
                        testId="thresholds-items-expand"
                        icon={<MaterialIcon name={isExpanded ? 'expand_less' : 'expand_more'} />}
                        noColor
                    />
                )}
            </div>
            {isExpanded && (
                <div className={styles.table}>
                    {items.map(item => {
                        const dropdownOptions: MenuItem[] = [
                            {
                                onClick: () => redirect(item.id),
                                text: txt('CustomThresholds.BuildingSettings'),
                                requiredRoleLevel: roleRestrictions.editBuilding,
                                requiredGroupTypes: [],
                            },
                            {
                                onClick: () => onRemoveThresholds(item.id),
                                text: txt('CustomThresholds.RemoveLocationThresholds'),
                                requiredRoleLevel: roleRestrictions.editBuilding,
                                requiredGroupTypes: [],
                            },
                        ];
                        return (
                            <div key={item.id} className={styles.row}>
                                <div className={styles.cellGroup}>
                                    <div className={classNames(styles.cell, styles.overflow)}>{item.name}</div>
                                    <div className={styles.cell}>
                                        {item.sensors.map((sensor, index) => (
                                            <span key={sensor}>
                                                {txt(sensor)}
                                                {index < item.sensors.length - 1 && ', '}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <DropdownOptions
                                        options={dropdownOptions}
                                        id={`option_${item.id}`}
                                        dotMenu
                                        customButton={<MaterialIcon name="more_horiz" />}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CustomThresholdSection;
